import { Box, Typography, Paper, Grid, Chip, Avatar, IconButton, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import type { ChipColor } from '@/types/chip';
import { Category, Close, School, Description, Info, Schedule } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import type { QualificationRecord } from '../shared/types';

const STATUS_COLORS: Record<string, ChipColor> = {
  Active: 'success',
  Inactive: 'warning',
};

export const QualificationTypeDetail = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { data: qualifications } = useGetList('qualifications');

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      const linkedQualifications = (qualifications || []).filter(
        (q: QualificationRecord) => (q.qualificationType as any)?.id === record.id || q.qualificationType === record.id,
      ).slice(0, 10);

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/qualification-types')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Qualification Type"
            description="A category of qualification (e.g., FET Certificate, PGCE, Bachelor of Education)."
            icon={<Category />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #6a1b9a, #8e24aa)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: 'secondary.main', fontSize: 28 }}>
                <Category />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.name}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.category}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                  <Chip label={`${linkedQualifications.length} linked qualification${linkedQualifications.length !== 1 ? 's' : ''}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Info color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Type Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{record.name}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Typography variant="body1">{record.category || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{record.description || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Schedule color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Metadata</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body1">
                      {record.metadata?.createdAt ? new Date(record.metadata.createdAt).toLocaleDateString() : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Created By</Typography>
                    <Typography variant="body1">{record.metadata?.createdBy || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body1">
                      {record.metadata?.updatedAt ? new Date(record.metadata.updatedAt).toLocaleDateString() : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Updated By</Typography>
                    <Typography variant="body1">{record.metadata?.updatedBy || '—'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          <Paper elevation={0} sx={{ mt: 3 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Linked Qualifications" />
            </Tabs>
            <Box sx={{ p: 3 }}>
              {activeTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <School color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>Qualifications Using This Type</Typography>
                    <Chip label={`${linkedQualifications.length} records`} size="small" sx={{ ml: 1 }} />
                    {linkedQualifications.length > 0 && (
                      <Button size="small" sx={{ ml: 'auto', textTransform: 'none' }} onClick={() => navigate('/qualifications')}>
                        View All
                      </Button>
                    )}
                  </Box>
                  {linkedQualifications.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Tutor</TableCell>
                          <TableCell>Issuer</TableCell>
                          <TableCell>Issued Date</TableCell>
                          <TableCell>Expiry Date</TableCell>
                          <TableCell>Verified</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {linkedQualifications.map((q: QualificationRecord) => (
                          <TableRow
                            key={q.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/qualifications/${q.id}/show`)}
                          >
                            <TableCell>{q.name}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: 'underline', textUnderlineOffset: 2, color: 'primary.main' }}>
                                {(q as any).tutorName || (q.tutor as any)?.name || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>{q.issuer || '—'}</TableCell>
                            <TableCell>{q.issuedDate || '—'}</TableCell>
                            <TableCell>{q.expiryDate || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={q.verified ? 'Verified' : 'Pending'}
                                size="small"
                                color={q.verified ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No qualifications use this type yet. Qualifications are linked to this type when created or edited.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }} />
  );
};
